import { useState } from 'react'
import type { JSX } from 'react'
import { Link } from 'react-router'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { SiteChip } from '@/settings/SiteChip'
import { useSettingsSite } from '@/settings/site'
import { UptimeForm } from '@/settings/uptime/form'
import type { UptimeChoice } from '@/settings/uptime/form'
import { describeInterval } from '@/settings/uptime/intervals'
import { summariseUptime } from '@/settings/uptime/summary'
import { may, useActiveTenant } from '@/tenant/context'
import { SettingRow } from '@/ui/SettingRow'
import { Empty, ErrorState, Loading } from '@/ui/states'
import { useToast } from '@/ui/toast-context'
import { useOneEditing } from '@/ui/useOneEditing'

/**
 * Settings › SEO › Uptime — per site, hence the chip.
 *
 * Settings consolidation, batch 2 (2026-09-15). The control was the last column
 * of the uptime table on `/seo/sites`, which keeps the table's figures and now
 * shows the choice as text. `discovery:manage` is what `app/v1/uptime.py` puts
 * on `PUT` and `DELETE`: operating the product rather than purchasing it.
 *
 * The three branches of a save are the three routes: `DELETE` for *Off*, `PUT`
 * with `enabled: false` for *Paused*, `PUT` with the interval otherwise.
 */
export function UptimePanel(): JSX.Element {
  const brand = useBrand()
  const { membership } = useActiveTenant()
  const editable = may(membership, 'discovery:manage')
  const site = useSettingsSite()
  const one = useOneEditing()
  const toast = useToast()
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const siteId = site.site?.id ?? null

  const uptime = useQuery(
    (signal) =>
      siteId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/sites/{site_id}/uptime', {
              params: { path: { site_id: siteId } },
              signal,
            }),
          ),
    [siteId],
  )
  const save = useMutation((input: { siteId: string; body: Schema<'MonitorIn'> }) =>
    call(() =>
      api.PUT('/api/v1/seo/sites/{site_id}/uptime', {
        params: { path: { site_id: input.siteId } },
        body: input.body,
      }),
    ),
  )
  const stop = useMutation((id: string) =>
    call(() =>
      api.DELETE('/api/v1/seo/sites/{site_id}/uptime', { params: { path: { site_id: id } } }),
    ),
  )

  return (
    <div className="ar-stack">
      <div className="ar-row">
        <h2 className="ar-h2">Uptime</h2>
        <span className="ar-card__spacer" />
        <SiteChip site={site} />
      </div>

      {site.status === 'loading' || (siteId !== null && uptime.state.status === 'loading') ? (
        <Loading label="Loading the uptime checks" lines={2} />
      ) : null}
      {site.status === 'error' && site.failure !== null ? (
        <ErrorState failure={site.failure} onRetry={site.retry} />
      ) : null}
      {uptime.state.status === 'error' ? (
        <ErrorState failure={uptime.state.failure} onRetry={uptime.refetch} />
      ) : null}
      {site.status === 'ready' && site.site === null ? (
        <Empty
          title="No site yet"
          body={`Add your website first. ${brand.productName} has nothing to watch until it knows where your pages live.`}
          action={
            <Link className="ar-btn ar-btn--primary ar-btn--sm" to="/seo/sites">
              Go to Sites
            </Link>
          }
        />
      ) : null}

      {site.site !== null && uptime.state.status === 'ready' && uptime.state.data !== null ? (
        <SettingRow
          key={site.site.id}
          label="Uptime checks"
          summary={summariseUptime(uptime.state.data.monitor)}
          savedAt={savedAt}
          editable={editable}
          editing={one.editing === 'uptime'}
          onEdit={() => {
            one.open('uptime')
          }}
          onCancel={one.close}
        >
          <UptimeForm
            monitor={uptime.state.data.monitor}
            domain={site.site.domain}
            submitLabel="Save"
            onSubmit={async (choice: UptimeChoice) => {
              const domain = site.site?.domain ?? ''
              const id = site.site?.id ?? ''
              // *Off* on a site that is not watched: nothing to delete (the API
              // would answer 204 anyway), so the row just closes.
              if (choice.kind === 'off' && uptime.state.data?.monitor === null) {
                one.close()
                toast.show({
                  kind: 'info',
                  title: `${domain} is not watched`,
                  body: 'Nothing to change.',
                })
                return { ok: true as const, data: undefined }
              }
              const result =
                choice.kind === 'off'
                  ? await stop.run(id)
                  : await save.run({
                      siteId: id,
                      body: {
                        url: choice.url,
                        enabled: choice.enabled,
                        interval_seconds: choice.seconds,
                      },
                    })
              if (result.ok) {
                setSavedAt(new Date().toISOString())
                one.close()
                uptime.refetch()
                toast.show({
                  kind: 'success',
                  title:
                    choice.kind === 'off'
                      ? `Stopped watching ${domain}`
                      : choice.enabled
                        ? `Checking ${domain} ${describeInterval(choice.seconds).toLowerCase()}`
                        : `Paused the checks on ${domain}`,
                  body:
                    choice.kind === 'off'
                      ? 'Its checks and their history have been discarded.'
                      : choice.enabled
                        ? `${brand.productName} will email you the first time a check does not answer.`
                        : 'Nothing is being fetched. Its history and interval are kept.',
                })
              }
              return result
            }}
          />
        </SettingRow>
      ) : null}
    </div>
  )
}
